import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Alert, AuthCard, Button, Field } from '../components/ui'
import { commonsConfig } from '../config'
import { errorCode } from '../lib/api'
import { useLogin, useResendConfirmation } from '../lib/session'

const linkStyle = 'font-medium underline underline-offset-4'

// L'ecran ne decide de rien : il suit l'etape indiquee par le serveur.
export default function LoginPage() {
  const navigate = useNavigate()
  const login = useLogin()
  const resend = useResendConfirmation()
  const { expired } = useSearch({ from: '/login' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    resend.reset()

    login.mutate(
      { email, password },
      {
        onSuccess: (response) => {
          if (response.next === 'mfa') {
            void navigate({ to: '/mfa' })
          } else if (response.next === 'password-setup') {
            void navigate({ to: '/password/setup' })
          } else {
            void navigate({ to: '/' })
          }
        },
      },
    )
  }

  const emailNotConfirmed = errorCode(login.error) === 'email-not-confirmed'

  return (
    <AuthCard title={commonsConfig().appName} subtitle="Connectez-vous a votre espace.">
      {expired === true && !login.isPending && (
        <div className="mb-4">
          <Alert tone="info">
            Votre session a expire apres {commonsConfig().sessionMinutes} minutes d'inactivite.
            Reconnectez-vous.
          </Alert>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <Field
          label="Adresse e-mail"
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        {/* Pas d'attribut required : l'administrateur initial se connecte une
            premiere fois sans mot de passe, et choisit le sien ensuite. */}
        <Field
          label="Mot de passe"
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {login.isError && <Alert>{login.error.message}</Alert>}

        {emailNotConfirmed && (
          <div className="space-y-2">
            {resend.isSuccess ? (
              <Alert tone="success">
                Demande enregistree : si l'adresse {email} attend encore sa confirmation, un nouveau
                lien lui est envoye. Pensez a verifier les courriers indesirables.
              </Alert>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={resend.isPending}
                onClick={() => resend.mutate(email)}
              >
                Renvoyer l'e-mail de confirmation
              </Button>
            )}
          </div>
        )}

        <Button type="submit" disabled={login.isPending}>
          {login.isPending ? 'Verification...' : 'Se connecter'}
        </Button>
      </form>

      <div className="mt-6 space-y-2 text-center text-sm text-neutral-600 dark:text-neutral-400">
        <p>
          <Link to="/forgot-password" className={linkStyle}>
            Mot de passe oublie ?
          </Link>
        </p>
        <p>
          Pas encore de compte ?{' '}
          <Link to="/register" className={linkStyle}>
            Creer un compte
          </Link>
        </p>
      </div>
    </AuthCard>
  )
}
